import React from 'react';
import { SkillDetailVO } from '../services/aiAssistant';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

interface SkillPopoverProps {
  open: boolean;
  /** 公用技能：Agent 默认绑定，只读，不可删除 */
  sharedIds: number[];
  /** 个人技能：用户自定义配置，可增删 */
  personalIds: number[];
  /** 本次会话启用的子集（可跨公用 + 个人） */
  enabledIds: number[];
  detailMap: Record<number, SkillDetailVO>;
  anchorRef: React.RefObject<HTMLElement>;
  onOpenDialog: () => void;
  /** 点击行：toggle 本次启用 */
  onToggleEnabled: (id: number) => void;
  /** × 按钮：从配置中删除（仅个人组） */
  onRemoveFromConfig: (id: number) => void;
  onClose: () => void;
}

const PREVIEW_COUNT = 5;
const POPOVER_WIDTH = 280;
const GAP = 6;

export default function SkillPopover({
  open,
  sharedIds,
  personalIds,
  enabledIds,
  detailMap,
  anchorRef,
  onOpenDialog,
  onToggleEnabled,
  onRemoveFromConfig,
  onClose
}: SkillPopoverProps) {
  const [sharedExpanded, setSharedExpanded] = useState(false);
  const [personalExpanded, setPersonalExpanded] = useState(false);
  const [pos, setPos] = useState<{ left: number; bottom: number } | null>(null);

  const enabledSet = useMemo(() => new Set(enabledIds), [enabledIds]);

  useEffect(() => {
    if (!open) {
      setSharedExpanded(false);
      setPersonalExpanded(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const compute = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = rect.right - POPOVER_WIDTH;
      if (left < 8) left = 8;
      if (left + POPOVER_WIDTH > vw - 8) left = vw - 8 - POPOVER_WIDTH;
      const bottom = vh - rect.top + GAP;
      setPos({ left, bottom });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, anchorRef]);

  if (!open || !pos) return null;

  const isEmpty = sharedIds.length === 0 && personalIds.length === 0;

  const renderGroup = (
    label: string,
    dotColor: string,
    rowBg: string,
    rowBorder: string,
    rowColor: string,
    ids: number[],
    expanded: boolean,
    setExpanded: (v: boolean) => void,
    readOnly: boolean
  ) => {
    if (!ids.length) return null;
    const showAll = expanded || ids.length <= PREVIEW_COUNT;
    const visible = showAll ? ids : ids.slice(0, PREVIEW_COUNT);
    return (
      <div style={{ marginBottom: 4 }}>
        <div
          style={{
            fontSize: 11,
            color: '#8C8C8C',
            margin: '8px 0 4px',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor }} />
          <span>{label}</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {visible.map((id) => {
            const detail = detailMap[id];
            const offline = !detail;
            const text = detail?.title || detail?.name || `Skill #${id}`;
            const isEnabled = enabledSet.has(id);
            return (
              <div
                key={id}
                onClick={() => !offline && onToggleEnabled(id)}
                style={{
                  padding: '6px 8px',
                  borderRadius: 6,
                  fontSize: 13,
                  color: offline ? '#8c8c8c' : rowColor,
                  background: isEnabled ? rowBg : 'transparent',
                  border: `1px solid ${isEnabled ? rowBorder : 'transparent'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: offline ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={(e) => {
                  if (!isEnabled && !offline) e.currentTarget.style.background = '#f5f5f7';
                }}
                onMouseLeave={(e) => {
                  if (!isEnabled) e.currentTarget.style.background = 'transparent';
                }}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={offline ? 'Skill 已下线' : text}
                >
                  {text}
                </span>
                {isEnabled && (
                  <span
                    style={{
                      fontSize: 13,
                      lineHeight: 1,
                      color: rowColor,
                      flexShrink: 0
                    }}
                    title="本次启用"
                  >
                    ✓
                  </span>
                )}
                {!readOnly && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveFromConfig(id);
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#ff4d4f')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#bfbfbf')}
                    title="从配置中删除"
                    style={{
                      fontSize: 14,
                      lineHeight: 1,
                      cursor: 'pointer',
                      color: '#bfbfbf',
                      flexShrink: 0
                    }}
                  >
                    ×
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {ids.length > PREVIEW_COUNT && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            style={{
              display: 'block',
              width: '100%',
              marginTop: 4,
              padding: '6px',
              border: 'none',
              background: 'transparent',
              color: '#8c8c8c',
              fontSize: 12,
              cursor: 'pointer'
            }}
          >
            {expanded ? '收起 ∧' : '展开更多 ∨'}
          </button>
        )}
      </div>
    );
  };

  return createPortal(
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1150 }} />
      <div
        style={{
          position: 'fixed',
          left: pos.left,
          bottom: pos.bottom,
          zIndex: 1151,
          background: '#fff',
          borderRadius: 10,
          border: '1px solid #efefef',
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          width: POPOVER_WIDTH,
          padding: '14px 16px 12px'
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1d2129', marginBottom: 10 }}>工具配置</div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 4
          }}
        >
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Skill</span>
          <button
            type="button"
            onClick={onOpenDialog}
            title="添加 Skill"
            style={{
              width: 22,
              height: 22,
              borderRadius: 4,
              fontSize: 16,
              border: 'none',
              background: 'transparent',
              color: '#8c8c8c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            ＋
          </button>
        </div>
        {isEmpty ? (
          <div style={{ padding: '12px 0', fontSize: 12, color: '#bfbfbf', textAlign: 'center' }}>
            还未配置 Skill，点击 + 添加
          </div>
        ) : (
          <>
            {renderGroup(
              '公用',
              '#1677ff',
              '#E6F0FF',
              '#BFD7FF',
              '#1677ff',
              sharedIds,
              sharedExpanded,
              setSharedExpanded,
              true
            )}
            {renderGroup(
              '个人',
              '#FF7A1A',
              '#FFF0E0',
              '#FFD9B3',
              '#FF7A1A',
              personalIds,
              personalExpanded,
              setPersonalExpanded,
              false
            )}
          </>
        )}
      </div>
    </>,
    document.body
  );
}
